import React, { useState, useEffect } from 'react';
import { auth } from '../../../firebase';
import { toast } from 'sonner';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TaxSettings = () => {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate]     = useState('');
  const [error, setError]         = useState('');

  // ── Load current settings ────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const token = await auth.currentUser?.getIdToken(true);
        const res   = await fetch(`${API}/api/invoices/settings/deposit`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load');
        const data = await res.json();
        setTaxEnabled(!!data.taxEnabled);
        // Show the rate only if it has been configured (taxEnabled or rate > 0)
        if (data.taxEnabled || data.taxRate > 0) {
          setTaxRate(String(data.taxRate ?? ''));
        } else {
          setTaxRate('');
        }
      } catch (e) {
        console.error('TaxSettings load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError('');
    const rate = parseFloat(taxRate || '0');

    if (taxEnabled) {
      if (isNaN(rate) || taxRate === '') {
        setError('Enter a valid tax rate (use 0 for explicitly exempt).');
        return;
      }
      if (rate < 0) {
        setError('Tax rate cannot be negative.');
        return;
      }
      if (rate > 30) {
        setError('Tax rate looks too high — double-check the value.');
        return;
      }
    }

    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res   = await fetch(`${API}/api/invoices/settings/tax`, {
        method:  'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`,
        },
        body: JSON.stringify({
          taxEnabled,
          taxRate: taxEnabled ? rate : 0,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Tax settings saved');
    } catch {
      setError('Failed to save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-400">Loading tax settings…</p>;
  }

  return (
    <div className="space-y-5" data-testid="tax-settings-section">
      {/* ── Toggle ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label className="text-sm font-medium text-gray-900">
            Apply sales tax to quotes
          </Label>
          <p className="text-xs text-gray-500 mt-0.5 max-w-sm">
            When enabled, tax is computed on new quotes and snapshotted on every
            invoice at creation time. Existing records are not affected.
          </p>
        </div>
        <Switch
          data-testid="tax-enabled-toggle"
          checked={taxEnabled}
          onCheckedChange={(val) => {
            setTaxEnabled(val);
            setError('');
          }}
        />
      </div>

      {/* ── Rate input (shown only when enabled) ────────────────────────── */}
      {taxEnabled && (
        <div className="space-y-1.5">
          <Label htmlFor="tax-rate-input" className="text-sm font-medium text-gray-900">
            Tax rate (%)
          </Label>
          <div className="flex items-center gap-2 max-w-xs">
            <Input
              id="tax-rate-input"
              data-testid="tax-rate-input"
              type="number"
              min="0"
              max="30"
              step="0.01"
              placeholder="e.g. 8.5"
              value={taxRate}
              onChange={(e) => {
                setTaxRate(e.target.value);
                setError('');
              }}
              className="w-32"
            />
            <span className="text-sm text-gray-500 select-none">%</span>
          </div>
          <p className="text-xs text-gray-400">
            Set to 0 to mark quotes as explicitly tax-exempt (a $0.00 tax line will
            appear). Leave tax disabled entirely if you do not collect sales tax.
          </p>
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <p className="text-xs text-red-600" data-testid="tax-settings-error">
          {error}
        </p>
      )}

      {/* ── Save ────────────────────────────────────────────────────────── */}
      <Button
        data-testid="save-tax-settings-btn"
        size="sm"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save Tax Settings'}
      </Button>
    </div>
  );
};

export default TaxSettings;
